import { useCallback, useEffect, useRef, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";
import { pub, countOnce, mediaUrl } from "@/lib/api";
import { isDevHost } from "@/lib/site";
import { Shell, useSiteConfig } from "./Shell";
import { Clock, LogAudio, fit, maxChars, noise, rnd } from "./logEngine";
import { KeyLine, Scramble } from "./LogText";
import { EggText, ReplyInput, RewardVideo, ZeroEvent } from "./LogExtras";

const KEEP = 40;
const BANNER = "This page has been reported. 333 Helpers is cleaning it up for you ♡";
const ZERO = "you stayed. good. they don't expect patience. 333.";
const reducedMotion = () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
const after = (ms, fn, list) => { const id = setTimeout(fn, ms); list.push(id); return id; };

function useSubjectSerial() {
  const [serial, setSerial] = useState(null);
  useEffect(() => {
    const t = new URLSearchParams(window.location.search).get("t");
    if (!t) return;
    pub.get("/log-subject", { params: { t } }).then((r) => setSerial(r.data.serial)).catch(() => {});
  }, []);
  return serial;
}

// Streams lines (noise, M's fragments, the recurring key line, the personal FLAGGED line) into a 40-line window.
function useStream(started, paused, fragments, serial, onKey) {
  const [lines, setLines] = useState([]);
  const st = useRef({ id: 0, clock: new Clock(), sinceFrag: 0, fragGap: 12, nextKey: 0, nextMe: 0 });
  const live = useRef({ paused, fragments, serial, onKey });
  live.current = { paused, fragments, serial, onKey };
  useEffect(() => {
    if (!started) return undefined;
    const s = st.current, timers = [], slow = reducedMotion() ? 2 : 1;
    s.nextKey = Date.now() + rnd(6000, 9000);
    s.nextMe = Date.now() + rnd(10000, 14000);
    const tick = () => {
      const now = Date.now(), cap = maxChars(), L = live.current;
      let line = null;
      if (now >= s.nextKey) {
        line = { kind: "key" };
        s.nextKey = now + rnd(18000, 25000);
      } else if (L.serial && now >= s.nextMe && !L.paused) {
        line = { kind: "me", text: fit(`${L.serial}  FLAGGED`, cap) };
        s.nextMe = now + rnd(27000, 33000);
      } else if (!L.paused && s.sinceFrag >= s.fragGap && L.fragments.length) {
        line = { kind: "frag", text: fit(L.fragments[Math.floor(Math.random() * L.fragments.length)], cap) };
        s.sinceFrag = 0;
        s.fragGap = 10 + Math.floor(Math.random() * 6);
      } else if (!L.paused) {
        line = { kind: "noise", text: noise(cap) };
        s.sinceFrag += 1;
      }
      if (line) {
        s.id += 1;
        const full = { ...line, id: s.id, ts: s.clock.next() };
        setLines((ls) => [...ls, full].slice(-KEEP));
      }
      after(rnd(600, 1400) * slow, tick, timers);
    };
    after(500, tick, timers);
    return () => timers.forEach(clearTimeout);
  }, [started]);
  const push = useCallback((line) => {
    const s = st.current;
    s.id += 1;
    setLines((ls) => [...ls, { ...line, id: s.id, ts: s.clock.next() }].slice(-KEEP));
  }, []);
  const dropTop = useCallback(() => {
    setLines((ls) => {
      const i = ls.findIndex((l) => !l.gone);
      if (i < 0) return ls;
      const id = ls[i].id;
      setTimeout(() => setLines((x) => x.filter((l) => l.id !== id)), 520);
      return ls.map((l, j) => (j === i ? { ...l, gone: true } : l));
    });
  }, []);
  return [lines, dropTop, push];
}

// 333 Helpers intrusion at ~60 s, then every few minutes: banner, gentle deletions, M's glitch, collapse.
function useHelpers(started, dropTop, texts, hold, sched) {
  const [banner, setBanner] = useState("none");
  const [text, setText] = useState(BANNER);
  const pool = useRef(texts);
  pool.current = texts;
  const holdRef = useRef(hold);
  holdRef.current = hold;
  const sch = useRef(sched);
  sch.current = sched || { enabled: true, first_s: 60, min_s: 150, max_s: 240 };
  useEffect(() => {
    if (!started) return undefined;
    const timers = [];
    let del = null;
    const run = () => {
      if (holdRef.current) { after(15000, run, timers); return; }  // never during the INTEGRITY 3% event
      const p = pool.current.length ? pool.current : [BANNER];
      setText(p[Math.floor(Math.random() * p.length)]);
      setBanner("in");
      del = setInterval(dropTop, 700);
      after(8000, () => { clearInterval(del); setBanner("glitch"); }, timers);
      after(8700, () => setBanner("out"), timers);
      after(9300, () => setBanner("none"), timers);
      after(rnd(sch.current.min_s, sch.current.max_s) * 1000, run, timers);
    };
    if (!sch.current.enabled) return undefined;  // Controls → banner off
    after(sch.current.first_s * 1000, run, timers);
    return () => { timers.forEach(clearTimeout); clearInterval(del); };
  }, [started, dropTop]);
  return [banner, text];
}

function useIntegrity(started) {
  // Preview only: ?integrity=5 starts low so the 3% event can be tested without waiting ~7 minutes.
  const [n, setN] = useState(() => {
    const q = parseInt(new URLSearchParams(window.location.search).get("integrity"), 10);
    return isDevHost() && q >= 3 && q <= 31 ? q : 31;
  });
  useEffect(() => {
    if (!started) return undefined;
    const timers = [];
    const step = () => { setN((x) => Math.max(3, x - 1)); after(rnd(13000, 17000), step, timers); };
    after(rnd(8000, 12000), step, timers);
    return () => timers.forEach(clearTimeout);
  }, [started]);
  return [n, setN];
}

// Admin test link (?test=zero&z=<30-min signed token>): fires the INTEGRITY 3% event ~3 s after tapping start.
function useZeroTest() {
  const [ok, setOk] = useState(false);
  useEffect(() => {
    const u = new URL(window.location.href);
    const z = u.searchParams.get("z");
    if (u.searchParams.get("test") !== "zero" || !z) return;
    u.searchParams.delete("test"); u.searchParams.delete("z");
    window.history.replaceState(null, "", u);
    pub.get("/zero-test", { params: { z } }).then((r) => setOk(!!r.data.ok)).catch(() => {});
  }, []);
  return ok;
}

function useJitter(started) {
  const [on, setOn] = useState(false);
  useEffect(() => {
    if (!started || reducedMotion()) return undefined;
    const timers = [];
    const go = () => { setOn(true); after(40, () => setOn(false), timers); after(rnd(5000, 14000), go, timers); };
    after(rnd(4000, 8000), go, timers);
    return () => timers.forEach(clearTimeout);
  }, [started]);
  return on;
}

function Line({ l, last }) {
  const tone = { noise: "text-[color:var(--w1-muted)] opacity-75", frag: "text-[color:var(--w1-text)]", me: "text-[color:var(--w1-amber)]",
    reply: "text-[color:var(--w1-text)]", "reply-bad": "text-[color:var(--w1-muted)]", egg: "text-[color:var(--w1-blue)]", hint: "text-[color:var(--w1-text)]" }[l.kind];
  return (
    <div data-testid={`log-line-${l.kind}`} className={`log-line whitespace-nowrap overflow-hidden ${l.gone ? "log-line-gone" : ""}`}>
      <span className="text-[color:var(--w1-amber)] opacity-70">{l.ts}</span>{" "}
      {l.kind === "key" ? <KeyLine onDecoded={l.onKey} /> : l.kind === "egg" ? (
        <span data-testid="log-egg-line" className={tone}><EggText text={l.text} /></span>
      ) : (
        <span data-testid={{ frag: "log-fragment", me: "log-personal-line", reply: "log-reply-line", "reply-bad": "log-reply-noise", egg: "log-egg-line", hint: "log-input-hint-line" }[l.kind]} className={tone}><Scramble text={l.text} /></span>
      )}
      {last && <span data-testid="terminal-cursor" className="w1-cursor ml-1" />}
    </div>
  );
}

function HelpersBanner({ state, text }) {
  if (state === "none") return null;
  return (
    <div data-testid="helpers-banner" data-state={state} className={`helpers-banner helpers-${state}`}>
      <span className="helpers-text">{text}</span>
    </div>
  );
}

function StartScreen({ onStart }) {
  return (
    <button type="button" data-testid="log-start-button" onClick={onStart}
      className="flex flex-1 flex-col items-center justify-center gap-4 px-6 text-center tracking-[0.3em] text-[color:var(--w1-blue)] crt-glow">
      <span className="text-sm sm:text-base">INTERCEPTED TRANSMISSION</span>
      <span className="text-[11px] text-[color:var(--w1-muted)] animate-pulse">— TAP TO DECRYPT —</span>
    </button>
  );
}

export default function LogPage() {
  const cfg = useSiteConfig();
  const serial = useSubjectSerial();
  const [started, setStarted] = useState(false);
  const [muted, setMuted] = useState(false);
  const audio = useRef(null);
  const [banner, setBannerShim] = useState("none");
  const [zero, setZero] = useState("pending");  // pending → on → done (at most once per visit)
  const onKey = useCallback(() => audio.current?.chirp(), []);
  const [lines, dropTop, push] = useStream(started, banner === "in" || zero === "on", cfg?.log_fragments || [], serial, onKey);
  const [helpers, bannerText] = useHelpers(started, dropTop, cfg?.helpers_banners || [], zero === "on", cfg?.helpers);
  useEffect(() => setBannerShim(helpers), [helpers]);
  const [integrity, setIntegrity] = useIntegrity(started);
  const zeroTest = useZeroTest();
  const [zeroPhase, setZeroPhase] = useState("");
  const reward = useRef(null);
  useEffect(() => { if (integrity <= 3 && zero === "pending") { setZero("on"); countOnce("log_zero"); } }, [integrity, zero]);
  useEffect(() => {
    if (!started || !zeroTest) return undefined;
    const id = setTimeout(() => setZero((z) => (z === "pending" ? "on" : z)), 3000);
    return () => clearTimeout(id);
  }, [started, zeroTest]);
  const zeroDone = useCallback(() => { setZero("done"); setZeroPhase(""); setIntegrity(31); }, [setIntegrity]);
  const onReply = useCallback((text, bad) => push({ kind: bad === "egg" ? "egg" : bad ? "reply-bad" : "reply", text: fit(text, maxChars()) }), [push]);
  const onHint = useCallback((text) => push({ kind: "hint", text: fit(text, maxChars()) }), [push]);
  const jitter = useJitter(started);
  const box = useRef(null);
  useEffect(() => () => audio.current?.stop(), []);
  useEffect(() => countOnce("log_visit"), []);
  useEffect(() => { if (box.current) box.current.scrollTop = box.current.scrollHeight; }, [lines]);

  const start = () => {
    setStarted(true);
    audio.current = new LogAudio();
    audio.current.start(!!cfg?.has_static).catch(() => {});
    const v = reward.current;  // unlock the reward video inside this tap so it can play with sound later (phones)
    if (v) { v.muted = true; v.play().then(() => { v.pause(); v.currentTime = 0; v.muted = false; }).catch(() => { v.muted = false; }); }
  };
  const toggle = () => { setMuted((m) => { audio.current?.setMuted(!m); if (reward.current && zeroPhase === "video") reward.current.muted = !m; return !m; }); };
  useEffect(() => { if (reward.current && zeroPhase === "video") reward.current.muted = muted; }, [zeroPhase, muted]);

  return (
    <Shell testid="transmission-log-page">
      <div className={`crt log-crt flex flex-col ${jitter ? "crt-jitter" : ""}`} data-testid="log-crt">
        <HelpersBanner state={helpers} text={bannerText} />
        {zero === "on" && <ZeroEvent text={cfg?.log_zero_text || ZERO} onDone={zeroDone} onPhase={setZeroPhase} audio={audio.current} video={cfg?.has_log_reward ? reward : null} />}
        {cfg?.has_log_reward && <RewardVideo vref={reward} src={mediaUrl("log_reward")} showing={zero === "on" && zeroPhase === "video"} />}
        {!started ? <StartScreen onStart={start} /> : (
          <>
            <button type="button" onClick={toggle} data-testid="log-mute-toggle" aria-label={muted ? "Unmute" : "Mute"}
              className="absolute right-3 top-3 z-[60] p-2 text-[color:var(--w1-muted)] transition-colors hover:text-[color:var(--w1-blue)]">
              {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
            </button>
            <div data-testid="transmission-log-terminal" className={`crt-glow ${zero === "done" ? "log-fade-in" : ""} flex min-h-0 flex-1 flex-col px-4 pt-8 text-[11px] leading-relaxed sm:px-10 sm:pt-14 sm:text-[13px]`}>
              <div data-testid="log-integrity" className="mb-5 tracking-[0.25em] text-[color:var(--w1-text)] opacity-90 w1-display crt-flicker">
                TRANSMISSION LOG // 010230 // INTEGRITY {integrity}%
              </div>
              <div ref={box} className="log-window min-h-0 flex-1 overflow-hidden">
                {lines.map((l, i) => <Line key={l.id} l={l.kind === "key" ? { ...l, onKey } : l} last={i === lines.length - 1} />)}
              </div>
              <ReplyInput started={started} rules={cfg?.log_replies || []} egg={cfg?.log_egg} ghostPhrases={cfg?.log_ghost_phrases} hint={cfg?.log_input_hint} prompt={cfg?.log_input_prompt} onReply={onReply} onHint={onHint} />
            </div>
          </>
        )}
      </div>
    </Shell>
  );
}
